import type { InlineModel, Location } from '@flipp/shopper-ad-web-schemas';
import {
  asNumber,
  DeviceService,
  environment,
  type EnvironmentType,
  PostMessageEvent,
  REQUEST_PREFIX,
  type StorefrontConfig,
  Tags,
} from '@flipp/shopper-ad-web-shared';
import { scrollInterval } from './config';
import { resizeGamContainer } from './GAMHelpers';
import type { MobileSwipeDynamicModuleConfig } from './MobileSwipeDynamicModule';
import { calculateGeomData } from './geom';
import { isInGoogleAdsIframe, thisIframeInParent } from './iframeHelpers';
import { throttle } from './throttle';
import { watchAdmixerViewable } from './admixerViewable';

// HTML wrapper that inlines mobile-swipe.min.js — the iframe src must be an
// HTML document, not a raw JS file, for the browser to execute the IIFE.
const MOBILE_SWIPE_CDN_URL =
  'https://shopper.flipp.com/tag/js/ad-formats/mobile-swipe.html';

export type MobileSwipeDecision = {
  siteId?: string | number;
  zoneId?: string | number;
  adId?: string | number;
  campaignId?: string | number;
  advertiserId?: string | number;
  creativeId?: string | number;
  flightId?: string | number;
};

export type MobileSwipeAutoEngageConfig = {
  // Default: true (set by the runtime when omitted)
  enabled?: boolean;
  // Default: 3000ms (set by the runtime when omitted)
  intervalMs?: number;
};

export const resolveMobileSwipeCtaUrl = (
  ctaUrl?: string | null,
): string | undefined => {
  if (typeof ctaUrl !== 'string') return undefined;

  const trimmed = ctaUrl.trim();
  if (!trimmed) return undefined;

  try {
    const parsed = new URL(trimmed);

    return parsed.protocol === 'https:' ? parsed.toString() : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Mobile Swipe fills its containing element at the IAB 2:3 "Interstitial"
 * ratio, within a hard 300-450px width band. Outside that band the unit is
 * not IAB compliant, so a container under 300px renders nothing at all
 * rather than a non-compliant unit.
 */
export const MOBILE_SWIPE_MIN_WIDTH = 300;
export const MOBILE_SWIPE_MAX_WIDTH = 450;
export const MOBILE_SWIPE_ASPECT_RATIO = 1.5;

/**
 * CF-738: test override for the frame height, from the
 * `mobileSwipeFrameHeight` LaunchDarkly flag. Lets a slice of traffic render
 * Mobile Swipe as tall as Fixed Height (e.g. 300x600) to measure how much the
 * unit's height drives EVs. Only whole pixels inside the IAB band's height
 * range are accepted; anything else keeps the 2:3 frame.
 */
export const MOBILE_SWIPE_MIN_HEIGHT = 450;
export const MOBILE_SWIPE_MAX_HEIGHT = 675;

export const resolveMobileSwipeFrameHeight = (
  value: unknown,
): number | undefined => {
  if (typeof value !== 'number' || !Number.isInteger(value)) {
    return undefined;
  }
  if (value < MOBILE_SWIPE_MIN_HEIGHT || value > MOBILE_SWIPE_MAX_HEIGHT) {
    return undefined;
  }

  return value;
};

export const resolveMobileSwipeFrameSize = (
  containerWidth: number,
  heightOverride?: number,
): { width: number; height: number } | null => {
  const measured = Math.round(containerWidth);
  if (!Number.isFinite(measured) || measured < MOBILE_SWIPE_MIN_WIDTH) {
    return null;
  }

  const width = Math.min(measured, MOBILE_SWIPE_MAX_WIDTH);
  const height =
    resolveMobileSwipeFrameHeight(heightOverride) ??
    Math.round(width * MOBILE_SWIPE_ASPECT_RATIO);

  return { width, height };
};

/**
 * The runtime's auto-swipe interval when none is passed. Mirrors
 * DEFAULT_INTERVAL_MS in ad-formats (packages/mobile-swipe/src/runtime/
 * auto-engage.ts); keep the two in sync. Only used to report the interval.
 */
export const MOBILE_SWIPE_AUTO_ENGAGE_DEFAULT_INTERVAL_MS = 3000;

export const MOBILE_SWIPE_EV_TIMER_MAX_S = 30;
export const resolveMobileSwipeEvTimerSeconds = (
  evTimerMs?: string | number | null,
): number | undefined => {
  const ms = asNumber(evTimerMs, 8000);
  if (ms <= 0) return undefined;

  const seconds = ms / 1000;

  return Math.min(seconds, MOBILE_SWIPE_EV_TIMER_MAX_S);
};

export type MobileSwipeIframeConfig = {
  publicationGlobalId: string;
  sessionId: string;
  siteId: number;
  zoneId: number;
  environment: EnvironmentType;
  appVersion: string;
  site?: string;
  integrationType?: string;
  decision?: MobileSwipeDecision;
  autoEngage?: MobileSwipeAutoEngageConfig;
  ctaUrl?: string;
  evTimer?: number;
  campaignIntlFmt?: string;
  /** DVM Module trafficked on the campaign. See `resolveMobileSwipeDynamicModule`. */
  dynamicModule?: MobileSwipeDynamicModuleConfig;
  device: {
    type?: string;
    model?: string;
    osName?: string;
    osVersion?: string;
    browser?: string;
    browserVersion?: string;
  };
  geoData: {
    postal_code?: string;
    latitude?: number;
    longitude?: number;
    country?: string;
    region?: string;
    accuracy_radius?: number;
  };
  legacyPublication?: {
    flyerId?: number;
    flyerRunId?: number;
    flyerTypeId?: number;
    merchantId?: number;
    sfmlUuid?: string;
  };
};

type MobileSwipeExperienceParams = {
  targetEl: HTMLElement;
  publicationGlobalId: string;
  siteId: number;
  zoneId: number;
  sessionId?: string;
  geoData?: Readonly<Location>;
  storefront?: Readonly<
    Pick<
      StorefrontConfig,
      | 'flyer_id'
      | 'flyer_run_id'
      | 'flyer_type_id'
      | 'merchant_id'
      | 'sfml_uuid'
    >
  >;
  decision?: Readonly<MobileSwipeDecision>;
  inlineDecision?: Readonly<InlineModel>;
  site?: string;
  integrationType?: string;
  autoEngage?: Readonly<MobileSwipeAutoEngageConfig>;
  /** https CTA destination. See {@link resolveMobileSwipeCtaUrl}. */
  ctaUrl?: string;
  evTimer?: number;
  campaignIntlFmt?: string;
  /** DVM Module. See `resolveMobileSwipeDynamicModule`. */
  dynamicModule?: Readonly<MobileSwipeDynamicModuleConfig>;
  onAdClick?: () => null;
  /**
   * CF-738 test override, straight from the LD settings blob, so it is
   * validated by {@link resolveMobileSwipeFrameHeight} rather than trusted.
   */
  frameHeight?: unknown;
};

export class MobileSwipeExperience {
  private readonly targetEl: HTMLElement;
  private readonly config: MobileSwipeIframeConfig;
  private readonly inlineDecision?: Readonly<InlineModel>;
  private readonly location?: Readonly<Location>;
  private readonly onAdClick?: () => null;
  private iframe?: HTMLIFrameElement;
  private scrollHandler?: () => void;
  private resizeObserver?: ResizeObserver;
  private resizeHandler?: () => void;
  private tagsSent = false;
  private hiddenByRuntime = false;
  private appliedGamBreakout?: string;
  private containerWindow: Window = window;
  private containerElement: Element;
  private readonly frameHeight?: number;

  constructor({
    targetEl,
    publicationGlobalId,
    siteId,
    zoneId,
    sessionId,
    geoData,
    storefront,
    decision,
    inlineDecision,
    site,
    integrationType,
    autoEngage,
    ctaUrl,
    evTimer,
    campaignIntlFmt,
    dynamicModule,
    onAdClick,
    frameHeight,
  }: Readonly<MobileSwipeExperienceParams>) {
    this.targetEl = targetEl;
    this.onAdClick = onAdClick;
    this.frameHeight = resolveMobileSwipeFrameHeight(frameHeight);
    this.containerElement = targetEl;
    this.inlineDecision = inlineDecision;
    this.location = geoData;
    const deviceInfo = DeviceService.getDeviceInfo();
    this.config = {
      publicationGlobalId,
      siteId,
      zoneId,
      sessionId: sessionId ?? '',
      environment,
      appVersion: '__appVersion',
      site,
      integrationType,
      decision,
      autoEngage,
      ctaUrl,
      evTimer,
      campaignIntlFmt,
      dynamicModule,
      device: {
        type: deviceInfo.type,
        model: deviceInfo.systemModel,
        osName: deviceInfo.system,
        osVersion: deviceInfo.systemVersion,
        browser: deviceInfo.browser,
        browserVersion: deviceInfo.browserVersion,
      },
      geoData: geoData
        ? {
            postal_code: geoData.postal_code,
            latitude: geoData.latitude,
            longitude: geoData.longitude,
            country: geoData.country,
            region: geoData.region,
            accuracy_radius: geoData.accuracy_radius,
          }
        : {},
      legacyPublication: storefront
        ? {
            flyerId: storefront.flyer_id,
            flyerRunId: storefront.flyer_run_id,
            flyerTypeId: storefront.flyer_type_id,
            merchantId: storefront.merchant_id,
            sfmlUuid: storefront.sfml_uuid,
          }
        : undefined,
    };
  }

  /** @returns whether the slot was filled. */
  render(): boolean {
    window.addEventListener('message', this.handlePostMessage);
    this.resolveContainer();
    this.registerScroll();
    this.registerResize();
    this.applyFrameSize();

    return !!this.iframe;
  }

  /**
   * Builds the frame. Deferred until the container can host a compliant unit,
   * so a too-narrow slot never loads the runtime or fires its impressions.
   */
  private mountFrame(): HTMLIFrameElement {
    if (this.inlineDecision && !this.tagsSent) {
      this.tagsSent = true;
      Tags.addTagsFromCampaignResponse(
        this.inlineDecision,
        { startCompact: false },
        this.location,
      );
    }

    const iframe = document.createElement('iframe');
    iframe.className = 'flippxp-mobile-swipe-frame';
    iframe.style.border = 'none';
    iframe.style.display = 'block';
    iframe.style.maxWidth = '100%';
    iframe.style.margin = '0 auto';
    iframe.style.borderRadius = '8px';
    iframe.style.overflow = 'hidden';
    iframe.setAttribute('scrolling', 'no');

    // Encode config the same way Experience.ts does, so the iframe can read it
    const encodedConfig =
      'flipp-' + btoa(encodeURIComponent(JSON.stringify(this.config)));
    iframe.setAttribute('name', encodedConfig);
    iframe.setAttribute('src', MOBILE_SWIPE_CDN_URL);

    this.iframe = iframe;
    this.targetEl.appendChild(iframe);
    watchAdmixerViewable(iframe, this.inlineDecision);
    if (this.inlineDecision) {
      Tags.sendServedImpressionEvent(this.inlineDecision);
    }

    return iframe;
  }

  /**
   * Sizes the frame from its containing element, mounting it once the
   * container is wide enough and hiding it if the container later drops
   * below the compliance floor.
   */
  private readonly applyFrameSize = () => {
    const size = resolveMobileSwipeFrameSize(
      this.targetEl.clientWidth,
      this.frameHeight,
    );
    if (!size) {
      if (this.iframe) {
        this.iframe.style.display = 'none';
      }

      return;
    }

    this.applyGamBreakout(size);

    const iframe = this.iframe ?? this.mountFrame();
    // The runtime's own hide wins over a re-measure
    if (!this.hiddenByRuntime) {
      iframe.style.display = 'block';
    }
    iframe.style.width = `${size.width}px`;
    iframe.style.height = `${size.height}px`;
  };

  private readonly applyGamBreakout = (
    size: Readonly<{ width: number; height: number }>,
  ) => {
    if (!isInGoogleAdsIframe()) {
      return;
    }

    const applied = `${size.width}x${size.height}`;
    if (this.appliedGamBreakout === applied) {
      return;
    }

    resizeGamContainer(size.width, size.height);
    this.appliedGamBreakout = applied;
  };

  /** Re-sizes on container resize and orientation change. */
  private registerResize() {
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.applyFrameSize);
      this.resizeObserver.observe(this.targetEl);

      return;
    }

    this.resizeHandler = this.applyFrameSize;
    window.addEventListener('resize', this.resizeHandler);
  }

  private resolveContainer() {
    const frameInParent = thisIframeInParent();
    if (!frameInParent) {
      return;
    }

    this.containerWindow = window.parent;
    this.containerElement = frameInParent;
  }

  private registerScroll() {
    this.scrollHandler = throttle(
      () => {
        this.sendInlineGeometry();

        return null;
      },
      scrollInterval,
      { leading: false, trailing: true },
    );

    this.containerWindow.addEventListener('scroll', this.scrollHandler, {
      passive: true,
    });
  }

  private readonly handlePostMessage = (event: MessageEvent) => {
    const data = event.data as { type?: string; value?: number };
    if (
      !this.iframe?.contentWindow ||
      event.source !== this.iframe.contentWindow
    ) {
      return;
    }

    if (
      data?.type === 'iframe-height-resize' &&
      typeof data.value === 'number'
    ) {
      this.iframe.style.height = `${data.value}px`;
    } else if (data?.type === 'hide-iframe') {
      this.hiddenByRuntime = true;
      this.iframe.style.display = 'none';
    } else if (data?.type === 'experience-open') {
      Tags.sendOpen();
    } else if (data?.type === 'experience-engagement') {
      Tags.sendEngagement();
    } else if (data?.type === 'experience-click') {
      this.onAdClick?.();
    } else if (
      data?.type ===
      REQUEST_PREFIX + PostMessageEvent.INLINE_GEOMETRY
    ) {
      this.sendInlineGeometry();
    }
  };

  private readonly sendInlineGeometry = () => {
    if (!this.iframe) {
      return;
    }

    const geom = calculateGeomData(
      this.containerElement,
      this.iframe,
      this.containerWindow,
    );
    if (geom == null) {
      return;
    }

    this.iframe.contentWindow?.postMessage(
      { type: PostMessageEvent.INLINE_GEOMETRY, data: geom },
      '*',
    );
  };

  destroy() {
    window.removeEventListener('message', this.handlePostMessage);
    if (this.scrollHandler) {
      this.containerWindow.removeEventListener('scroll', this.scrollHandler);
      this.scrollHandler = undefined;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = undefined;
    }
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
      this.resizeHandler = undefined;
    }
    this.iframe?.remove();
  }
}
